import React, { useState, useEffect } from 'react';
import { useSeason } from '../../context/SeasonContext';
import {
  getPaymentCollectionStatus,
  checkSeasonComplete,
  checkPlayoffStatus,
  generatePaymentCollection,
  sendPaymentReminders,
  markPaymentAsPaid,
  getPaymentMethods,
  getAvailableSeasons
} from '../../adminApi';
import PaymentMessageReviewModal from './PaymentMessageReviewModal';
import styles from '../../styles/AdminDashboard.module.css';
import paymentStyles from './PaymentCollectionPanel.module.css';

const PaymentCollectionPanel = () => {
  const { effectiveSeason } = useSeason();
  const [season, setSeason] = useState(effectiveSeason);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Status checks
  const [seasonComplete, setSeasonComplete] = useState(null);
  const [playoffStatus, setPlayoffStatus] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState(null);
  const [availableSeasons, setAvailableSeasons] = useState([]);
  
  // Action states
  const [generating, setGenerating] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  
  // Mark as paid modal state
  const [markingPaid, setMarkingPaid] = useState(null);
  const [selectedPaymentService, setSelectedPaymentService] = useState('venmo');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [useLLM, setUseLLM] = useState(true);

  useEffect(() => {
    loadData();
    loadSeasonChecks();
    loadAvailableSeasons();
  }, [season]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPaymentCollectionStatus(season);
      setRecords(response.data.records || []);
      setSummary(response.data.summary || null);
    } catch (err) {
      console.error('Error loading payment collection status:', err);
      setError(err.response?.data?.message || 'Failed to load payment collection status');
    } finally {
      setLoading(false);
    }
  };

  const loadSeasonChecks = async () => {
    try {
      const [seasonCheck, playoffCheck, methods] = await Promise.all([
        checkSeasonComplete(season),
        checkPlayoffStatus(season),
        getPaymentMethods()
      ]);
      setSeasonComplete(seasonCheck.data);
      setPlayoffStatus(playoffCheck.data);
      setPaymentMethods(methods.data);
    } catch (err) {
      console.error('Error loading season checks:', err);
    }
  };

  const loadAvailableSeasons = async () => {
    try {
      const response = await getAvailableSeasons();
      setAvailableSeasons(response.data || []);
    } catch (err) {
      console.error('Error loading seasons:', err);
    }
  };

  const handleGenerate = async () => {
    if (!window.confirm('Generate payment collection records for this season? This will create records for all users who owe money.')) {
      return;
    }

    try {
      setGenerating(true);
      setError(null);
      const response = await generatePaymentCollection(season);
      setSuccess(`✅ Payment collection generated: ${response.data.created} created, ${response.data.skipped} skipped, ${response.data.errors} errors`);
      await loadData();
    } catch (err) {
      console.error('Error generating payment collection:', err);
      setError(err.response?.data?.message || 'Failed to generate payment collection');
    } finally {
      setGenerating(false);
    }
  };

  const handleReviewAndSend = () => {
    setShowReviewModal(true);
  };

  const handleReviewModalClose = () => {
    setShowReviewModal(false);
  };

  const handleSendComplete = (result) => {
    setSuccess(`✅ Payment notifications sent: ${result.sent} sent, ${result.failed} failed`);
    loadData();
  };

  const handleSendReminders = async () => {
    if (!window.confirm('Send payment reminders to all unpaid users? This will resend payment messages to users who haven\'t paid yet.')) {
      return;
    }

    try {
      setSendingReminders(true);
      setError(null);
      const response = await sendPaymentReminders(season);
      setSuccess(`✅ Payment reminders sent: ${response.data.sent} sent, ${response.data.failed} failed`);
      await loadData();
    } catch (err) {
      console.error('Error sending payment reminders:', err);
      setError(err.response?.data?.message || 'Failed to send payment reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const handleMarkAsPaid = async (recordId) => {
    try {
      setError(null);
      await markPaymentAsPaid(recordId, selectedPaymentService);
      setSuccess(`✅ Payment marked as paid using ${selectedPaymentService}`);
      setMarkingPaid(null);
      setSelectedPaymentService('venmo');
      await loadData();
    } catch (err) {
      console.error('Error marking payment as paid:', err);
      setError(err.response?.data?.message || 'Failed to mark payment as paid');
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'paid':
        return '✅ Paid';
      case 'reminded':
        return '⏰ Reminded';
      case 'pending':
        return '⏳ Pending';
      default:
        return status;
    }
  };

  const canSendNotifications = seasonComplete?.isComplete && playoffStatus?.canProceed;
  const cannotSendReason = !seasonComplete?.isComplete 
    ? seasonComplete?.message || 'Season is not complete'
    : !playoffStatus?.canProceed
    ? `Playoff with ${playoffStatus?.participantCount} participants is still pending`
    : null;

  if (loading && !records.length) {
    return <div className={styles.loading}>Loading payment collection data...</div>;
  }

  return (
    <div className={paymentStyles.panel}>
      <h2>💰 Payment Collection</h2>
      
      {/* Season Selector */}
      <div className={paymentStyles.seasonSelector}>
        <label>Season:</label>
        <select 
          value={season} 
          onChange={(e) => setSeason(parseInt(e.target.value))}
        >
          {availableSeasons.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* Error and Success Messages */}
      {error && (
        <div className={styles.errorMessage}>
          ❌ {error}
        </div>
      )}
      {success && (
        <div className={`${styles.updateMessage} ${styles.success}`}>
          {success}
        </div>
      )}

      {/* Status Checks Section */}
      <div className={paymentStyles.statusSection}>
        <h3>Season Status</h3>
        <div className={paymentStyles.statusGrid}>
          <div className={paymentStyles.statusItem}>
            <strong>Season Complete:</strong>
            <div className={seasonComplete?.isComplete ? paymentStyles.statusComplete : paymentStyles.statusIncomplete}>
              {seasonComplete?.isComplete ? '✅ Yes' : '❌ No'}
              {!seasonComplete?.isComplete && seasonComplete?.remainingLocks !== undefined && (
                <div className={paymentStyles.statusDetail}>
                  {seasonComplete.remainingLocks} remaining locks
                </div>
              )}
            </div>
          </div>
          <div className={paymentStyles.statusItem}>
            <strong>Playoff Status:</strong>
            <div className={playoffStatus?.canProceed ? paymentStyles.statusCanProceed : paymentStyles.statusOnHold}>
              {playoffStatus?.canProceed ? '✅ Can Proceed' : '⏸️ On Hold'}
              {playoffStatus?.exists && (
                <div className={paymentStyles.statusDetail}>
                  {playoffStatus.participantCount} participants
                </div>
              )}
            </div>
          </div>
        </div>
        {cannotSendReason && (
          <div className={paymentStyles.warningBanner}>
            ⚠️ <strong>Cannot send notifications:</strong> {cannotSendReason}
          </div>
        )}
      </div>

      {/* Payment Methods Display */}
      {paymentMethods && (
        <div className={paymentStyles.paymentMethodsSection}>
          <h3>Payment Methods Configuration</h3>
          <div className={paymentStyles.paymentMethodsGrid}>
            {paymentMethods.venmo?.link && (
              <div className={paymentStyles.paymentMethodItem}>
                <strong>Venmo:</strong> <a href={paymentMethods.venmo.link} target="_blank" rel="noopener noreferrer">{paymentMethods.venmo.link}</a>
              </div>
            )}
            {paymentMethods.paypal?.link && (
              <div className={paymentStyles.paymentMethodItem}>
                <strong>PayPal:</strong> <a href={paymentMethods.paypal.link} target="_blank" rel="noopener noreferrer">{paymentMethods.paypal.link}</a>
              </div>
            )}
            {paymentMethods.cashapp?.link && (
              <div className={paymentStyles.paymentMethodItem}>
                <strong>CashApp:</strong> <a href={paymentMethods.cashapp.link} target="_blank" rel="noopener noreferrer">{paymentMethods.cashapp.link}</a>
              </div>
            )}
            {paymentMethods.zelle?.accountInfo && (
              <div className={paymentStyles.paymentMethodItem}>
                <strong>Zelle:</strong> {paymentMethods.zelle.accountInfo}
              </div>
            )}
          </div>
          <div className={paymentStyles.paymentMethodsNote}>
            ℹ️ Payment methods are configured via environment variables (development) or AWS Secrets Manager (production).
          </div>
        </div>
      )}

      {/* LLM Toggle */}
      <div className={paymentStyles.llmToggleSection}>
        <label className={paymentStyles.llmToggleLabel}>
          <input
            type="checkbox"
            checked={useLLM}
            onChange={(e) => setUseLLM(e.target.checked)}
          />
          <span>Use LLM for personalized payment messages</span>
        </label>
      </div>

      {/* Summary Statistics */}
      {summary && (
        <div className={paymentStyles.summarySection}>
          <h3>Summary Statistics</h3>
          <div className={paymentStyles.summaryGrid}>
            <div className={paymentStyles.summaryItem}>
              <div className={paymentStyles.summaryLabel}>Total Records</div>
              <div className={paymentStyles.summaryValue}>{summary.total_records}</div>
            </div>
            <div className={paymentStyles.summaryItem}>
              <div className={paymentStyles.summaryLabel}>Total Owed</div>
              <div className={`${paymentStyles.summaryValue} ${paymentStyles.owed}`}>${summary.total_owed?.toFixed(2) || '0.00'}</div>
            </div>
            <div className={paymentStyles.summaryItem}>
              <div className={paymentStyles.summaryLabel}>Total Paid</div>
              <div className={`${paymentStyles.summaryValue} ${paymentStyles.paid}`}>${summary.total_paid?.toFixed(2) || '0.00'}</div>
            </div>
            <div className={paymentStyles.summaryItem}>
              <div className={paymentStyles.summaryLabel}>Remaining</div>
              <div className={`${paymentStyles.summaryValue} ${paymentStyles.remaining}`}>${summary.total_remaining?.toFixed(2) || '0.00'}</div>
            </div>
            <div className={paymentStyles.summaryItem}>
              <div className={paymentStyles.summaryLabel}>Paid</div>
              <div className={`${paymentStyles.summaryValue} ${paymentStyles.paid}`}>{summary.paid_count || 0}</div>
            </div>
            <div className={paymentStyles.summaryItem}>
              <div className={paymentStyles.summaryLabel}>Pending</div>
              <div className={`${paymentStyles.summaryValue} ${paymentStyles.owed}`}>{summary.pending_count || 0}</div>
            </div>
            <div className={paymentStyles.summaryItem}>
              <div className={paymentStyles.summaryLabel}>Reminded</div>
              <div className={`${paymentStyles.summaryValue} ${paymentStyles.remaining}`}>{summary.reminded_count || 0}</div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className={paymentStyles.actionButtons}>
        <button
          onClick={handleGenerate}
          disabled={generating || records.length > 0}
          className={`${styles.saveButton} ${(generating || records.length > 0) ? paymentStyles.buttonDisabled : ''}`}
        >
          {generating ? 'Generating...' : '📊 Generate Payment Collection'}
        </button>
        <button
          onClick={handleReviewAndSend}
          disabled={!canSendNotifications || records.length === 0 || summary?.pending_count === 0}
          className={`${styles.saveButton} ${(!canSendNotifications || records.length === 0 || summary?.pending_count === 0) ? paymentStyles.buttonDisabled : paymentStyles.buttonSuccess}`}
        >
          📧 Review & Send Payment Notifications
        </button>
        <button
          onClick={handleSendReminders}
          disabled={!canSendNotifications || sendingReminders || records.length === 0 || (summary?.pending_count === 0 && summary?.reminded_count === 0)}
          className={`${styles.saveButton} ${(!canSendNotifications || records.length === 0 || (summary?.pending_count === 0 && summary?.reminded_count === 0)) ? paymentStyles.buttonDisabled : paymentStyles.buttonWarning}`}
        >
          {sendingReminders ? 'Sending...' : '⏰ Send Reminders'}
        </button>
        <button
          onClick={loadData}
          className={styles.cancelButton}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Payment Records Table */}
      {records.length > 0 ? (
        <div className={paymentStyles.tableContainer}>
          <table className={paymentStyles.table}>
            <thead>
              <tr>
                <th>Username</th>
                <th className={paymentStyles.center}>Record</th>
                <th className={paymentStyles.center}>Rank</th>
                <th className={paymentStyles.center}>Base Owed</th>
                <th className={paymentStyles.center}>Prize Winnings</th>
                <th className={paymentStyles.center}>Final Owed</th>
                <th className={paymentStyles.center}>Status</th>
                <th className={paymentStyles.center}>Reminders</th>
                <th className={paymentStyles.center}>Payment Service</th>
                <th className={paymentStyles.center}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr key={record.id} className={record.payment_status === 'paid' ? paymentStyles.paidRow : ''}>
                  <td>{record.username}</td>
                  <td className={paymentStyles.center}>{record.wins}-{record.losses}</td>
                  <td className={paymentStyles.center}>
                    {record.final_rank || 'N/A'}
                    {record.is_top_3 && <span className={paymentStyles.rankNote}> (Top 3)</span>}
                  </td>
                  <td className={paymentStyles.center}>${record.base_amount_owed?.toFixed(2) || '0.00'}</td>
                  <td className={`${paymentStyles.center} ${paymentStyles.amountWinnings}`}>
                    ${record.prize_winnings?.toFixed(2) || '0.00'}
                  </td>
                  <td className={`${paymentStyles.center} ${paymentStyles.bold} ${paymentStyles.amountOwed}`}>
                    ${record.final_amount_owed?.toFixed(2) || '0.00'}
                  </td>
                  <td className={paymentStyles.center}>
                    <span className={`${paymentStyles.statusBadge} ${paymentStyles[record.payment_status] || ''}`}>
                      {getStatusLabel(record.payment_status)}
                    </span>
                  </td>
                  <td className={paymentStyles.center}>
                    <div className={paymentStyles.reminderCount}>
                      {record.reminder_count || 0}
                      {record.last_reminder_sent_at && (
                        <div className={paymentStyles.reminderDate}>
                          {new Date(record.last_reminder_sent_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className={paymentStyles.center}>
                    {record.payment_service_used ? (
                      <span className={paymentStyles.paymentService}>{record.payment_service_used}</span>
                    ) : (
                      <span className={paymentStyles.emptyState}>—</span>
                    )}
                  </td>
                  <td className={paymentStyles.center}>
                    {record.payment_status !== 'paid' && (
                      <button
                        onClick={() => setMarkingPaid(record)}
                        className={paymentStyles.markPaidButton}
                      >
                        ✓ Mark Paid
                      </button>
                    )}
                    {record.payment_status === 'paid' && record.payment_confirmed_at && (
                      <div className={paymentStyles.reminderDate}>
                        {new Date(record.payment_confirmed_at).toLocaleDateString()}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={paymentStyles.noDataMessage}>
          No payment collection records found for this season. Click "Generate Payment Collection" to create records.
        </div>
      )}

      {/* Mark as Paid Modal */}
      {markingPaid && (
        <div className={paymentStyles.modalOverlay}>
          <div className={paymentStyles.modalContent}>
            <h3>Mark Payment as Paid</h3>
            <p><strong>User:</strong> {markingPaid.username}</p>
            <p><strong>Amount:</strong> ${markingPaid.final_amount_owed?.toFixed(2)}</p>
            <div className={paymentStyles.modalFieldGroup}>
              <label className={paymentStyles.modalLabel}>
                Payment Service:
              </label>
              <select
                value={selectedPaymentService}
                onChange={(e) => setSelectedPaymentService(e.target.value)}
                className={paymentStyles.modalSelect}
              >
                <option value="venmo">Venmo</option>
                <option value="paypal">PayPal</option>
                <option value="cashapp">CashApp</option>
                <option value="zelle">Zelle</option>
              </select>
            </div>
            <div className={paymentStyles.modalActions}>
              <button
                onClick={() => {
                  setMarkingPaid(null);
                  setSelectedPaymentService('venmo');
                }}
                className={paymentStyles.buttonCancel}
              >
                Cancel
              </button>
              <button
                onClick={() => handleMarkAsPaid(markingPaid.id)}
                className={paymentStyles.buttonConfirm}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      <PaymentMessageReviewModal
        isOpen={showReviewModal}
        onClose={handleReviewModalClose}
        season={season}
        onSendComplete={handleSendComplete}
      />
    </div>
  );
};

export default PaymentCollectionPanel;
